import {
  Component, OnInit, AfterViewInit, ViewChild, OnDestroy
} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {FormBuilder, FormGroup, FormControl, Validators} from '@angular/forms';
import {StsNotification} from '../../../models/sts-notification.model';
import {StsNotificationType} from '../../../models/sts-notification-type';
import {StsHttpService} from 'app/services/sts-http.service';
import {Question} from '../../../models/question.model';
import {QAndA} from '../../../models/q-and-a.model';
import {StsPortalStatusService} from '../../../services/sts-portal-status.service';
import {StsSessionTimeoutService} from '../../../services/sts-session-timeout.service';
import {ActivatedRoute, Router} from '@angular/router';
import {StsTenantService} from 'app/services/sts-tenant.service';
import {StsPortalStatus}  from '../../../models/sts-portal-status';
import {StsButtonComponent} from 'app/components/common/sts-button/sts-button.component';
import * as _ from 'lodash';

@Component({
  selector: 'app-preselected-questions',
  templateUrl: './account-preselected-questions.component.html',
  styleUrls: ['./account-preselected-questions.component.scss']
})
export class AccountPreselectedQuestionsComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly numQAndAs = 3;

  readonly questionErrorPrefix: string = 'duplicateQuestion-';
  readonly answerErrorPrefix: string = 'duplicateAnswer-';

  readonly stsRequiredErrorCode: string = 'sts-field-error-answer-required';
  readonly stsRequiredErrorDesc: string = 'This is a required field';
  readonly stsMinLengthErrorCode: string = 'sts-field-error-answer-min-length';
  readonly stsMinLengthErrorDesc: string = 'Answer must be at least 3 characters long';
  readonly stsDuplicateAnswerErrorCode: string = 'sts-field-error-answer-duplicate';
  readonly stsDuplicateAnswerErrorDesc: string = 'Each answer must be unique';
  readonly stsDuplicateQuestionErrorCode: string = 'sts-field-error-question-duplicate';
  readonly stsDuplicateQuestionErrorDesc: string = 'Each question must be unique';


  newUserFlow: boolean;
  formSubmitSuccess = false;
  inConfirmState = false;
  isDisclaimerRequired = false;
  currentLocale = 'en-CA';
  questions: Question[] = [];
  qAndAList: QAndA[] = [];
  qAndAMap: {[id: string]: QAndA} = {};
  answerKeys: string[] = [];
  private viewLoaded = false;
  public errors: StsNotification[] = [];
  public termsLink: string = null;
  public numberOfQuestionsToConfigure: number = 3;
  qAndAForm: FormGroup;

  @ViewChild(StsButtonComponent) submitButton;

  constructor(private http: StsHttpService
    , private portalStatusService: StsPortalStatusService
    , private route: ActivatedRoute
    , private router: Router
    , private tenantService: StsTenantService
    , private stsSessionTimeoutService: StsSessionTimeoutService
    , private formBuilder: FormBuilder) {

    this.qAndAForm = this.formBuilder.group({}, {
      validator: this.getQAndAFormValidator(this.answerKeys, this.qAndAList)
    });
  }

  isFrench() {
    return this.currentLocale && this.currentLocale.toLowerCase().includes('fr');
  }

  getQuestionText(question: Question) {
    return this.isFrench() ? question.textFr : question.textEn;
  }

  validateFlow() {
    this.newUserFlow = false;
    this.route.params.subscribe(params => {
      this.portalStatusService.getPortalStatus().subscribe(
        (portalStatus: StsPortalStatus) => {
          let isSelfRegFlow = portalStatus.isSelfRegFlow;
          let isInRecoveryMode = portalStatus.isInRecoveryMode;
          if (params['new'] === 'new') {
            if (!isSelfRegFlow && !isInRecoveryMode) {
              console.log('User attempting to execute \'set security questions\' wihtout permission! Redirecting to login.');
              this.router.navigateByUrl('/account/signin');
              return;
            }
            this.newUserFlow = true;
          }
        });
    });
  }

  getAllSecurityQuestions(): Observable<Question[]> {
    return this.http.get<Question[]>('/api/my/securityquestions', null, res => {
      let questions: Question[] = [];
      let i = 0;
      for (const resQuestion of res) {
        let question = new Question();
        question.id = resQuestion.id;
        // TODO:temp fix, the fix would be getting local from browser and Ariel Security tenant SupportedLocales and
        // find the closest match.
        question.textEn = resQuestion.question['en-CA'] || resQuestion.question['en-US'];
        question.textFr = resQuestion.question['fr-CA'];
        question.error = new StsNotification();
        questions.push(question);
        i++;
      }
      return questions;
    })
  }

  populateForm(questions: Question[]) {
    this.questions = questions;
    for (let i = 0; i < this.numberOfQuestionsToConfigure; i++) {
      let qAndA = new QAndA();
      qAndA.question = new Question();
      qAndA.error = new StsNotification();
      qAndA.internalId = 'answer' + i; // simple id (can be anything)
      this.answerKeys.push(qAndA.internalId);
      this.qAndAList.push(qAndA);
    }
    for (let i = 0; i < this.numberOfQuestionsToConfigure; i++) {
      this.qAndAList[i].QuestionId = this.questions[i].id;
      this.qAndAList[i].question = this.questions[i];
      this.changeQuestionVisibility(this.qAndAList, this.qAndAList[i], this.questions[i]);
      this.qAndAMap[this.qAndAList[i].internalId] = this.qAndAList[i];
    }
    this.initForm();
    this.qAndAForm.updateValueAndValidity();
  }

  ngOnInit() {
    this.stsSessionTimeoutService.setTimeout();
    this.validateFlow();
    this.tenantService.getTenantSource().subscribe(tenant => {
      this.isDisclaimerRequired = tenant.isDisclaimerRequired;
      this.currentLocale = tenant.currentLocale;
      this.termsLink = '/Article/Print/' + tenant.clientCode.toLocaleLowerCase() + '-terms-of-use';
      this.numberOfQuestionsToConfigure = tenant.defaultSecurityPolicy.numberOfQuestionsToAnswer || 3;
      this.getAllSecurityQuestions()
        .subscribe(this.populateForm.bind(this));
    })
  }

  ngOnDestroy() {
    this.stsSessionTimeoutService.cancelTimeout();
  }

  private changeQuestionVisibility(qAndAList: QAndA[], changedQAndA: QAndA, selectedQuestion: Question) {
    for (let i = 0; i < qAndAList.length; i++) {
      let questionIds = qAndAList[i].hiddenQuestionIds = qAndAList[i].hiddenQuestionIds || [];
      let idIndex = questionIds.indexOf(changedQAndA.question.id);
      if (idIndex > -1) {
        questionIds[idIndex] = selectedQuestion.id;
      } else {
        questionIds.push(selectedQuestion.id);
      }

      qAndAList[i].hiddenQuestionIds = _.cloneDeep(questionIds);
    }
  }

  private findDuplicates(group: FormGroup, keys?: string[], qAndAList?: QAndA[]): string[] {
    let identicalEntryKeys: string[] = [];
    if (keys && keys.length < 2 || qAndAList && qAndAList.length < 2) {
      return identicalEntryKeys;
    }

    if (qAndAList) {
      for (let i = 0; i < qAndAList.length; i++) {
        for (let j = i + 1; j < qAndAList.length; j++) {
          if (qAndAList[i].question.id === qAndAList[j].question.id) {
            identicalEntryKeys.push(qAndAList[i].internalId);
            identicalEntryKeys.push(qAndAList[j].internalId);
          }
        }
      }
    } else {
      for (let i = 0; i < keys.length; i++) {
        for (let j = i + 1; j < keys.length; j++) {
          let firstControlVaue = _.get(group, 'controls[' + keys[i] + '].value', false);
          let secondControlVaue = _.get(group, 'controls[' + keys[j] + '].value', false);
          if (firstControlVaue && secondControlVaue && firstControlVaue === secondControlVaue) {
            identicalEntryKeys.push(keys[i]);
            identicalEntryKeys.push(keys[j]);
          }
        }
      }
    }

    return identicalEntryKeys;
  }

  private getQAndAFormValidator(answerKeys: string[], qAndAList: QAndA[]) {
    return (group: FormGroup): {[key: string]: any} => {
      if (!this.viewLoaded) {
        return;
      }

      // let duplicateQuestions: string[] = this.findDuplicates(group, null, qAndAList);
      let duplicateAnswers: string[] = this.findDuplicates(group, answerKeys);
      let errorMap = {};

      // for (let key of duplicateQuestions) {
      //   errorMap[this.questionErrorPrefix + key] = true;
      //   this.setError(this.qAndAMap[key], {'duplicateQuestion': true});
      // }

      for (let key of duplicateAnswers) {
        errorMap[this.answerErrorPrefix + key] = true;
        this.qAndAMap[key].error.code = this.stsDuplicateAnswerErrorCode;
        this.qAndAMap[key].error.description = this.stsDuplicateAnswerErrorDesc;
      }

      return errorMap;
    };
  }

  private initForm() {
    this.qAndAForm = this.formBuilder.group({}, {
      validator: this.getQAndAFormValidator(this.answerKeys, this.qAndAList)
    });

    for (let qAndA of this.qAndAList) {
      this.qAndAForm.addControl(qAndA.internalId, new FormControl('',
        [
          Validators.required,
          Validators.minLength(3)
        ]
      ));
    }
  }

  // getQuestionValidity(qAndA: QAndA) {
  //   return !this.qAndAForm.hasError(this.questionErrorPrefix + qAndA.internalId);
  // }

  getAnswerValidity(qAndA: QAndA) {
    return this.qAndAForm.controls[qAndA.internalId].valid
      && !this.qAndAForm.hasError(this.answerErrorPrefix + qAndA.internalId)
      || !this.qAndAForm.controls[qAndA.internalId].touched;
  }

  toggleConfirmState() {
    this.inConfirmState = !this.inConfirmState;
  }

  setQuestion(qAndA: QAndA, question: Question) {
    this.changeQuestionVisibility(this.qAndAList, qAndA, question);
    qAndA.question = question;
    // this.qAndAForm.updateValueAndValidity();
  }

  onSubmit() {
    this.validateAndSave();
  }

  onAnswerBlur(qAndA: QAndA) {
    this.setError(qAndA, this.qAndAForm.controls[qAndA.internalId].errors);
    // remove this because the reactive form validator will call this
    // this.getQAndAFormValidator(this.answerKeys, this.qAndAList)(this.qAndAForm);
    // this.ref.detectChanges();
  }

  initialSave() {
    for (let qAndA of this.qAndAList) {
      qAndA.answer = this.qAndAForm.controls[qAndA.internalId].value;
    }
    this.toggleConfirmState();
  }

  private composeSecurityQuestionsRequest(qAndAList: QAndA[]) {
    let questionIdsWithAnswers = [];
    for (let qAndA of qAndAList) {
      questionIdsWithAnswers.push({
        QuestionId: qAndA.question.id,
        Answer: qAndA.answer
      });
    }
    return {
      PreselectedQuestions: questionIdsWithAnswers
    };
  }

  validateAndSave() {
    this.errors = [];
    let url = '/api/my/securityquestions';

    // for testing failed/error state
    // url = '/api/users/{{userId}}/securityquestionsError';
    // url = '/api/users/{{userId}}/securityquestionsError2';
    // url = '/api/StsForcedError';

    this.submitButton.onSubmit();
    this.http.post(url, this.composeSecurityQuestionsRequest(this.qAndAList))
      .subscribe(a => {
          this.formSubmitSuccess = true;
          if (this.newUserFlow && this.isDisclaimerRequired) {
            this.router.navigateByUrl('/account/disclaimer');
          } else {
            this.submitButton.onSubmitFinished();
            let err = new StsNotification();
            err.code = 'sts-change-security-questions-success';
            err.level = StsNotificationType.Success;
            err.description = 'Your security questions have been saved';
            this.errors.push(err);
          }
        },
        err => {
          this.submitButton.onSubmitFinished();
          if (err.error && err.error.errors) {
            for (let i in err.error.errors) {
              let error = new StsNotification();
              console.log(i, err.error.errors[i]);
              error.code = err.error.errors[i].errorCode || 'sts-preselect-questions-error-message';
              error.description = err.error.errors[i].explanation || 'Error: ' + err.error.errors[i].errorCode;
              error.level = StsNotificationType.Error;
              this.errors.push(error);
            }
          } else {
            let error = new StsNotification();
            error.code = err['sts-code'] || 'sts-preselect-questions-error-message';
            error.description = err.error || 'An error has occurred!';
            error.level = StsNotificationType.Error;
            this.errors.push(error);
          }
        });
  }

  onContinue() {
    window.location.href = '/';
  }

  ngAfterViewInit() {
    this.viewLoaded = true;
    for (let qAndA of this.qAndAList) {
      let control = this.qAndAForm.controls[qAndA.internalId];
      control.valueChanges.subscribe(data => {
        this.setError(qAndA, control.errors);
      });
    }
  }

  private setError(qAndA: QAndA, errors) {
    try {
      if (!errors) {
        return;
      }
      if (errors.required) {
        qAndA.error.code = this.stsRequiredErrorCode;
      } else if (errors.minlength) {
        qAndA.error.code = this.stsMinLengthErrorCode;
      }
    } catch (ex) {
      console.log(ex);
    }
  }
}
